// Audio engine (rebuild.md §9.10): master → {music, ambience, sfx, voice}. Voice ducks music by 9 dB and
// ambience by 6 dB (200 ms attack, 600 ms release). Excerpts fade all game buses out. Buffers are decoded
// on demand into an LRU (≤ 40 MB). Ambience loops crossfade over 2 s. Unlocks on first gesture; suspends
// when the page is hidden. Missing files fall back to small synthesised placeholders for core SFX.
import { signal } from '@preact/signals';

export type Bus = 'music' | 'ambience' | 'sfx' | 'voice';

/** A playing voice line. */
export interface VoiceHandle {
  duration: number;
  ended: Promise<void>;
  stop: () => void;
}

interface AudioManifest {
  music: Record<string, { file: string; seconds?: number; loop?: boolean }>;
  sfx: Record<string, { file: string; seconds?: number; loop?: boolean }>;
  barks?: Record<string, { file: string; seconds?: number }>;
  gen?: Record<string, { file: string }>;
}

const DUCK_MUSIC = 10 ** (-9 / 20);
const DUCK_AMBIENCE = 10 ** (-6 / 20);
const LRU_BYTES = 40 * 1024 * 1024;

export const audioUnlocked = signal(false);

class Lru {
  private map = new Map<string, { buf: AudioBuffer; bytes: number }>();
  private total = 0;
  get(k: string): AudioBuffer | undefined {
    const v = this.map.get(k);
    if (!v) return undefined;
    this.map.delete(k);
    this.map.set(k, v);
    return v.buf;
  }
  put(k: string, buf: AudioBuffer): void {
    const bytes = buf.length * buf.numberOfChannels * 4;
    this.map.set(k, { buf, bytes });
    this.total += bytes;
    for (const [key, v] of this.map) {
      if (this.total <= LRU_BYTES) break;
      this.map.delete(key);
      this.total -= v.bytes;
    }
  }
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private buses = {} as Record<Bus, GainNode>;
  private duckers = {} as Record<'music' | 'ambience', GainNode>;
  private excerptGate!: GainNode;
  private cache = new Lru();
  private loading = new Map<string, Promise<AudioBuffer | null>>();
  private manifest: AudioManifest | null = null;
  private volumes: Record<Bus, number> = { music: 0.6, ambience: 0.5, sfx: 0.8, voice: 0.9 };
  private musicSrc: { src: AudioBufferSourceNode; gain: GainNode; id: string } | null = null;
  private ambienceLoop: { id: string; stop: () => void } | null = null;
  private focusLoop: { id: string; stop: () => void } | null = null;
  private voicesPlaying = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;

  /** Call from a user gesture (iOS needs this). */
  unlock(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.excerptGate = ctx.createGain();
    this.excerptGate.connect(ctx.destination);
    this.master.connect(this.excerptGate);
    for (const b of ['music', 'ambience', 'sfx', 'voice'] as Bus[]) {
      const g = ctx.createGain();
      g.gain.value = this.volumes[b];
      if (b === 'music' || b === 'ambience') {
        const d = ctx.createGain();
        g.connect(d);
        d.connect(this.master);
        this.duckers[b] = d;
      } else g.connect(this.master);
      this.buses[b] = g;
    }
    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden) void this.ctx.suspend();
      else void this.ctx.resume();
    });
    audioUnlocked.value = true;
    void this.loadManifest();
  }

  get unlocked(): boolean {
    return !!this.ctx;
  }

  setVolume(bus: Bus, v: number): void {
    this.volumes[bus] = v;
    const g = this.buses[bus];
    if (g && this.ctx) g.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
  }

  private async loadManifest(): Promise<AudioManifest | null> {
    if (this.manifest) return this.manifest;
    try {
      const r = await fetch('/audio/manifest.json');
      if (r.ok) this.manifest = (await r.json()) as AudioManifest;
    } catch {
      /* offline before first cache: placeholders only */
    }
    return this.manifest;
  }

  private async urlFor(kind: 'music' | 'sfx' | 'bark' | 'gen', id: string): Promise<string> {
    const m = await this.loadManifest();
    if (kind === 'bark') return m?.barks?.[id]?.file ?? `/audio/barks/${id}.mp3`;
    if (kind === 'music') return m?.music[id]?.file ?? `/audio/music/${id}.mp3`;
    if (kind === 'gen') return m?.gen?.[id]?.file ?? `/audio/gen/${id}.mp3`;
    return m?.sfx[id]?.file ?? m?.gen?.[id]?.file ?? `/audio/sfx/${id}.mp3`;
  }

  private async buffer(url: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    const hit = this.cache.get(url);
    if (hit) return hit;
    let p = this.loading.get(url);
    if (!p) {
      p = (async () => {
        try {
          const r = await fetch(url);
          if (!r.ok) return null;
          const buf = await this.ctx!.decodeAudioData(await r.arrayBuffer());
          this.cache.put(url, buf);
          return buf;
        } catch {
          return null;
        } finally {
          this.loading.delete(url);
        }
      })();
      this.loading.set(url, p);
    }
    return p;
  }

  /** Preload ids so first plays are instant. */
  async preload(items: { kind: 'music' | 'sfx' | 'bark'; id: string }[]): Promise<void> {
    await Promise.all(items.map(async (i) => this.buffer(await this.urlFor(i.kind, i.id))));
  }

  async sfx(id: string): Promise<void> {
    if (!this.ctx) return;
    const buf = await this.buffer(await this.urlFor('sfx', id));
    if (!buf) return this.placeholder(id);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.buses.sfx);
    src.start();
  }

  /** Start a voice line. Resolves to a handle once playing, or null if it can't play. */
  async voice(kind: 'bark' | 'gen', id: string): Promise<VoiceHandle | null> {
    if (!this.ctx) return null;
    const buf = await this.buffer(await this.urlFor(kind, id));
    if (!buf) return null;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.buses.voice);
    this.duck(true);
    this.voicesPlaying++;
    let resolveEnded!: () => void;
    const ended = new Promise<void>((r) => (resolveEnded = r));
    src.onended = () => {
      this.voicesPlaying--;
      if (this.voicesPlaying <= 0) this.duck(false);
      resolveEnded();
    };
    src.start();
    return {
      duration: buf.duration,
      ended,
      stop: () => {
        try {
          src.stop();
        } catch {
          /* already stopped */
        }
      },
    };
  }

  private duck(on: boolean): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    const apply = (m: number, a: number, tc: number) => {
      this.duckers.music.gain.setTargetAtTime(m, t, tc);
      this.duckers.ambience.gain.setTargetAtTime(a, t, tc);
    };
    if (on) apply(DUCK_MUSIC, DUCK_AMBIENCE, 0.2 / 3);
    else apply(1, 1, 0.6 / 3);
  }

  /** Music cue (openers/finales; Home loops). Crossfades from the current cue; stops any focus loop. */
  async music(id: string | null, opts: { loop?: boolean; fade?: number } = {}): Promise<void> {
    if (!this.ctx) return;
    const fade = opts.fade ?? 1.5;
    const t = this.ctx.currentTime;
    // any cue (or silence) replaces the focus loop
    this.focusLoop?.stop();
    this.focusLoop = null;
    if (this.musicSrc && this.musicSrc.id !== id) {
      const old = this.musicSrc;
      old.gain.gain.setTargetAtTime(0, t, fade / 3);
      setTimeout(() => old.src.stop(), fade * 1000 + 200);
      this.musicSrc = null;
    }
    if (!id || this.musicSrc?.id === id) return;
    const buf = await this.buffer(await this.urlFor('music', id));
    if (!buf || !this.ctx) return;
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.connect(this.buses.music);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = !!opts.loop;
    src.connect(gain);
    src.start();
    gain.gain.setTargetAtTime(1, this.ctx.currentTime, fade / 3);
    this.musicSrc = { src, gain, id };
  }

  /** Ambience bed, looped with a 2 s crossfade between repeats. */
  async ambience(id: string | null): Promise<void> {
    if (this.ambienceLoop?.id === id) return;
    this.ambienceLoop?.stop();
    this.ambienceLoop = null;
    if (!id || !this.ctx) return;
    const buf = await this.buffer(await this.urlFor('sfx', id));
    if (!buf || !this.ctx) return;
    this.ambienceLoop = { id, stop: this.loop(buf, this.buses.ambience, 2) };
  }

  /**
   * Focus music under gameplay (task 8.5, §8.3): a generated loop on the music bus, crossfaded over 3 s between
   * repeats. It replaces the stage's opener cue (which fades out); a new cue stops it. Resolves false if there is
   * no such loop.
   */
  async focus(id: string | null): Promise<boolean> {
    if (this.focusLoop?.id === id) return true;
    this.focusLoop?.stop();
    this.focusLoop = null;
    if (!id || !this.ctx) return false;
    const file = (await this.loadManifest())?.gen?.[id]?.file;
    if (!file) return false;
    const buf = await this.buffer(file);
    if (!buf || !this.ctx) return false;
    await this.music(null, { fade: 3 });
    this.focusLoop = { id, stop: this.loop(buf, this.buses.music, 3) };
    return true;
  }

  /** Loop a buffer on a bus with an equal crossfade between repeats; returns a fading stop. */
  private loop(buf: AudioBuffer, bus: GainNode, crossfade: number): () => void {
    const ctx = this.ctx!;
    const XF = Math.min(crossfade, buf.duration / 4);
    const out = ctx.createGain();
    out.connect(bus);
    let stopped = false;
    const nodes: AudioBufferSourceNode[] = [];
    const schedule = (when: number) => {
      if (stopped) return;
      const g = ctx.createGain();
      g.connect(out);
      g.gain.setValueAtTime(0, when);
      g.gain.linearRampToValueAtTime(1, when + XF);
      g.gain.setValueAtTime(1, when + buf.duration - XF);
      g.gain.linearRampToValueAtTime(0, when + buf.duration);
      const s = ctx.createBufferSource();
      s.buffer = buf;
      s.connect(g);
      s.start(when);
      nodes.push(s);
      const nextAt = when + buf.duration - XF;
      setTimeout(() => schedule(nextAt), Math.max(0, (nextAt - ctx.currentTime - 0.5) * 1000));
    };
    schedule(ctx.currentTime + 0.05);
    return () => {
      stopped = true;
      out.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
      for (const n of nodes) {
        try {
          n.stop(ctx.currentTime + 1.5);
        } catch {
          /* already stopped */
        }
      }
    };
  }

  /** Fade every game bus out while an excerpt plays (and back in after). */
  excerptMode(on: boolean): void {
    if (!this.ctx) return;
    this.excerptGate.gain.setTargetAtTime(on ? 0 : 1, this.ctx.currentTime, on ? 0.15 : 0.4);
  }

  /** Tiny synthesised stand-ins for core SFX until real files exist. */
  private placeholder(id: string): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const shapes: Record<string, [number, number, number][]> = {
      scanner_beep: [[880, 0, 0.08], [1320, 0.09, 0.1]],
      bad_beep: [[220, 0, 0.18]],
      warn_beep: [[660, 0, 0.12], [660, 0.2, 0.12]],
      stamp: [[90, 0, 0.12]],
      stamp_big: [[60, 0, 0.25]],
      holo_tag: [[1046, 0, 0.06]],
      crt_on: [[1500, 0, 0.04]],
    };
    const notes = shapes[id];
    if (!notes) return;
    for (const [f, at, dur] of notes) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      o.type = f < 120 ? 'triangle' : 'square';
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
      o.connect(g);
      g.connect(this.buses.sfx);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + dur + 0.02);
    }
  }
}

export const audio = new AudioEngine();
